import * as React from 'react'

import {
  Button,
  ChevronLeftIcon,
  ChevronRightIcon,
  createIdentityImageEdit,
  Dialog,
  IconButton,
  ImageEditorSurface,
  Input,
  isIdentityImageEdit,
  Label,
  Notice,
  SimpleSpinner,
  Slider,
  Text,
  ToggleGroup,
  Tooltip,
  VisuallyHidden,
} from '@buffer-mono/popcorn'
import type {
  ImageEditorAspectRatio,
  ImageEditorAspectRatioRange,
  ImageEditorSource,
  ImageEditorSurfaceAlternateInteraction,
} from '@buffer-mono/popcorn'

import { VideoThumbnailMaxSize } from '~publish/legacy/composer/composer/AppConstants'
import {
  RANGE_ALLOWABLE_MAX_LIMIT,
  RANGE_TOTAL_MAX,
} from '~publish/legacy/composer/composer/components/VideoThumbnailSlider'
import type {
  Image,
  Video,
} from '~publish/legacy/composer/composer/entities/factories'
import { resizeImageIfNeeded } from '~publish/legacy/composer/composer/utils/DOMUtils'
import { ComposerMediaEditorDiscardDialog } from '~publish/components/ComposerMediaEditor/ComposerMediaEditorDiscardDialog'

import { ComposerImageAltText } from './ComposerImageAltText/ComposerImageAltText'
import {
  ComposerImageWorkspaceFilmstrip,
  getImageWorkspaceFilmstripTabId,
} from './ComposerImageWorkspaceFilmstrip'
import type {
  ComposerImageWorkspaceFilmstripIssue,
  ComposerImageWorkspaceFilmstripItem,
} from './ComposerImageWorkspaceFilmstrip'
import { ComposerImageUserTagging } from './ComposerImageUserTagging/ComposerImageUserTagging'
import type {
  ComposerImageUserTaggingHandle,
  ComposerImageUserTaggingInteractionState,
} from './ComposerImageUserTagging/ComposerImageUserTagging'
import {
  createImageWorkspaceSession,
  getActiveVideoIndex,
  getActiveVideoWorkspaceItem,
  getDirtyVideoCount,
  getImageWorkspaceChangeSummary,
  getImageWorkspaceItemAt,
  getImageWorkspaceItemCount,
  getImageWorkspaceResult,
  getImageWorkspaceStagedUserTagCount,
  getImageWorkspaceUserTagProjections,
  imageWorkspaceSessionReducer,
  isImageWorkspaceItemDirty,
  isImageWorkspaceSessionDirty,
  isImageWorkspaceVideoActive,
  isVideoMedia,
} from './imageWorkspaceSession'
import type {
  ImageWorkspaceResult,
  ImageWorkspaceVideoItem,
} from './imageWorkspaceSession'
import type {
  ImageWorkspaceTrackingCloseRequest,
  ImageWorkspaceTrackingSession,
  ImageWorkspaceTrackingTask,
  ImageWorkspaceTrackingVisibleError,
} from './imageWorkspaceTracking'

import styles from './ComposerImageWorkspace.module.css'

import type { JSX } from 'react'

const APPLY_VISUAL_EDITS_BEFORE_GENERATING_ALT_TEXT =
  'Apply image edits before generating a description.'

export type ComposerImageWorkspaceReadySource = ImageEditorSource &
  Readonly<{
    width: number
    height: number
  }>

export type ComposerImageWorkspaceImage =
  | Readonly<{
      image: Image
      sourceStatus?: 'ready'
      editorSource: ComposerImageWorkspaceReadySource
    }>
  | Readonly<{
      image: Image
      sourceStatus: 'idle' | 'loading' | 'error'
      editorSource?: undefined
    }>

export type ComposerImageWorkspaceTask =
  | 'edit'
  | 'alt-text'
  | 'tagging'
  | 'thumbnail'

export type ComposerImageWorkspaceValidationIssue = Readonly<{
  image: Image
  message: string
}>

export type ComposerImageWorkspacePreparation =
  | Readonly<{ status: 'loading' }>
  | Readonly<{
      status: 'error'
      onRetry: () => void
      onUsePreviousEditor?: () => void
    }>

type ComposerImageWorkspaceStatus =
  | Readonly<{ state: 'idle' }>
  | Readonly<{ state: 'applying' }>
  | Readonly<{
      state: 'error'
      errorType:
        | 'imageRenderFailed'
        | 'replacementUploadFailed'
        | 'attachmentRemoved'
        | 'unknownApplyFailure'
      message: string
    }>

type EditorDraftValidity = Readonly<{
  activeIndex: number
  sourceSrc: string | undefined
  errorType: 'imageLoadFailed' | 'outputSizeInvalid' | null
  hasBeenReady: boolean
  isValid: boolean
  issue: string | null
}>

/**
 * A trailing video attachment. Video is never editable through this
 * workspace — no source-loading state is needed, only the video itself, for
 * display and identity checks.
 */
export type ComposerImageWorkspaceVideo = Readonly<{ video: Video }>

export type ComposerImageWorkspaceProps = Readonly<{
  open: boolean
  images: readonly ComposerImageWorkspaceImage[]
  videos?: readonly ComposerImageWorkspaceVideo[]
  initialMedia: Image | Video
  preparation?: ComposerImageWorkspacePreparation
  initialTask?: ComposerImageWorkspaceTask
  validationIssues?: readonly ComposerImageWorkspaceValidationIssue[]
  sharedRatiosAreLimited?: boolean
  aspectRatios?: readonly ImageEditorAspectRatio[]
  showFreeAspectRatio?: boolean
  freeAspectRatioRange?: ImageEditorAspectRatioRange
  altText?: Readonly<{
    maxLength: number
    onRequestSignup?: (trigger: HTMLButtonElement) => void
  }>
  tagging?: Readonly<{
    /** Reveals tagging on open and optionally starts placement when ready. */
    initialMode?: 'manage' | 'add'
    maxTagCount: number
  }>
  /** Presence signals the network supports replacing the video's thumbnail. */
  videoThumbnailEditing?: Readonly<{ canEditTitle: boolean }>
  status: ComposerImageWorkspaceStatus
  trackingSession?: ImageWorkspaceTrackingSession
  getReturnFocusTarget?: () => HTMLElement | null
  onRequestImageSource?: (image: Image) => Promise<boolean>
  onRequestTaggingSignup?: (trigger: HTMLButtonElement) => void
  onActiveMediaChange?: (media: Image | Video) => void
  onApply: (result: ImageWorkspaceResult) => void
  onCancel: () => void
}>

type ComposerImageWorkspaceContentProps = Omit<
  ComposerImageWorkspaceProps,
  'getReturnFocusTarget' | 'open' | 'preparation'
> &
  Readonly<{
    onCloseButtonClick: () => void
    taggingRef: React.RefObject<ComposerImageUserTaggingHandle>
  }>

type ComposerImageWorkspaceContentHandle = Readonly<{
  focusInitialTask: () => boolean
  requestDismiss: (method: ImageWorkspaceDismissMethod) => void
}>

type ImageWorkspaceDismissMethod = Extract<
  ImageWorkspaceTrackingCloseRequest,
  { closeReason: 'cancelled' | 'discarded' }
>['closeMethod']

type FilmstripFocusRequest = Readonly<{
  index: number
  key: number
}>

type WorkspaceNotice = Readonly<{
  variant: 'error' | 'warning'
  message: string
  action?: React.ReactNode
}>

type WorkspaceStageWarning = Readonly<{
  message: string
  reviewImageIndex?: number
}>

const INVALID_TAG_RESET_WARNING =
  'This tag has an invalid position. Reset crop & transform to continue.'

const THUMBNAIL_SEEK_FAILED_WARNING =
  "We couldn't read this video frame. Try a different time, or upload the video as an MP4 file."

const TASK_LABELS: Readonly<Record<ComposerImageWorkspaceTask, string>> = {
  edit: 'Edit',
  'alt-text': 'Alt Text',
  tagging: 'Tag People',
  thumbnail: 'Thumbnail',
}

const getTrackingTask = (
  task: ComposerImageWorkspaceTask,
): ImageWorkspaceTrackingTask => (task === 'alt-text' ? 'altText' : task)

const getAvailableTasks = (
  videoIsActive: boolean,
  hasThumbnailEditing: boolean,
  hasAltText: boolean,
  hasTagging: boolean,
): readonly ComposerImageWorkspaceTask[] => {
  if (videoIsActive) return hasThumbnailEditing ? ['thumbnail'] : []
  if (hasAltText && hasTagging) return ['edit', 'alt-text', 'tagging']
  if (hasAltText) return ['edit', 'alt-text']
  if (hasTagging) return ['edit', 'tagging']
  return ['edit']
}

const isComposerImageWorkspaceTask = (
  value: string,
): value is ComposerImageWorkspaceTask =>
  value === 'edit' ||
  value === 'alt-text' ||
  value === 'tagging' ||
  value === 'thumbnail'

const formatImageNumbers = (imageIndexes: readonly number[]): string =>
  imageIndexes
    .map((index) => index + 1)
    .reduce(
      (label, imageNumber, index) =>
        `${label}${index === 0 ? '' : index === imageIndexes.length - 1 ? ' and ' : ', '}${imageNumber}`,
      '',
    )

const getInactiveInvalidTagWarning = (
  imageIndexes: readonly number[],
  canRemoveTags: boolean,
): string => {
  const imageNumbers = formatImageNumbers(imageIndexes)

  if (imageIndexes.length === 1) {
    return canRemoveTags
      ? `Image ${imageNumbers} has a tag with an invalid position. Review it to remove the tag or reset its crop & transform.`
      : `Image ${imageNumbers} has a tag with an invalid position. Review it and reset its crop & transform to continue.`
  }

  return canRemoveTags
    ? `Images ${imageNumbers} have tags with invalid positions. Review each image to remove its tags or reset its crop & transform.`
    : `Images ${imageNumbers} have tags with invalid positions. Review each image and reset its crop & transform to continue.`
}

const getInactiveOutsideCropWarning = (
  entries: readonly Readonly<{ imageIndex: number; tagCount: number }>[],
): string => {
  const tagCount = entries.reduce((total, entry) => total + entry.tagCount, 0)
  const imageNumbers = formatImageNumbers(
    entries.map((entry) => entry.imageIndex),
  )

  return `${tagCount} ${tagCount === 1 ? 'tag' : 'tags'} outside the crop on ${entries.length === 1 ? 'image' : 'images'} ${imageNumbers} won’t be included when you apply changes.`
}

const getWorkspaceImageAtIndex = (
  images: readonly ComposerImageWorkspaceImage[],
  image: Image,
  index: number,
): ComposerImageWorkspaceImage => {
  const workspaceImage = images[index]
  if (workspaceImage?.image !== image) {
    throw new Error('Workspace images must remain in their original order')
  }

  return workspaceImage
}

const isWorkspaceImageReady = (
  workspaceImage: ComposerImageWorkspaceImage,
): workspaceImage is Extract<
  ComposerImageWorkspaceImage,
  Readonly<{ editorSource: ComposerImageWorkspaceReadySource }>
> => workspaceImage.editorSource !== undefined

const captureVideoFrame = (video: HTMLVideoElement): Promise<Blob | null> =>
  new Promise((resolve) => {
    const canvas = document.createElement('canvas')
    const { width, height } = resizeImageIfNeeded(VideoThumbnailMaxSize, {
      width: video.videoWidth,
      height: video.videoHeight,
    })
    canvas.width = width
    canvas.height = height
    canvas.getContext('2d')?.drawImage(video, 0, 0, width, height)
    canvas.toBlob(resolve, 'image/jpeg')
  })

type FrameCaptureResult =
  | Readonly<{ ok: true; blob: Blob }>
  | Readonly<{ ok: false }>

// Chrome silently fails to seek some QuickTime-brand .mov containers: no
// `seeked` event ever fires and `currentTime` never moves, even though the
// same H.264 stream seeks fine in an .mp4. Give a real seek this long to
// settle before treating it as failed — long enough for a slow connection to
// buffer ahead, short enough that the UI doesn't hang indefinitely.
const SEEK_TIMEOUT_MS = 4000
// A `seeked` event can also fire without landing on the requested time. Rather
// than trust the event alone, require currentTime to be within this many
// seconds of the target before capturing.
const SEEK_TIME_TOLERANCE_SECONDS = 0.5

/**
 * Seeks to `time` and resolves with a captured frame once the seek settles —
 * `currentTime` assignment is asynchronous, so capturing immediately after
 * setting it risks reading the previous frame. Resolves `{ ok: false }`
 * instead of hanging when the seek never lands, so the caller can surface
 * that rather than silently keep the previous frame.
 */
const seekAndCaptureVideoFrame = (
  video: HTMLVideoElement,
  time: number,
): Promise<FrameCaptureResult> =>
  new Promise((resolve) => {
    let timeoutId: ReturnType<typeof setTimeout>
    // The timeout can fire while an onSeeked-triggered capture is still
    // in flight (canvas.toBlob is async) — only the first to arrive settles.
    let settled = false

    const settle = (result: FrameCaptureResult): void => {
      if (settled) return
      settled = true
      clearTimeout(timeoutId)
      video.removeEventListener('seeked', onSeeked)
      // oxlint-disable-next-line promise/no-multiple-resolved -- settled guard above makes this idempotent, only the first caller resolves
      resolve(result)
    }

    const captureIfSeekLanded = (): void => {
      if (Math.abs(video.currentTime - time) > SEEK_TIME_TOLERANCE_SECONDS) {
        settle({ ok: false })
        return
      }
      void captureVideoFrame(video).then((blob) =>
        settle(blob ? { ok: true, blob } : { ok: false }),
      )
    }

    const onSeeked = (): void => captureIfSeekLanded()

    if (!video.seeking && video.currentTime === time) {
      captureIfSeekLanded()
      return
    }

    video.addEventListener('seeked', onSeeked)
    timeoutId = setTimeout(() => settle({ ok: false }), SEEK_TIMEOUT_MS)
    video.currentTime = time
  })

/**
 * Seeks only once the browser has caught up with the last request, instead
 * of assigning `currentTime` on every pointer-move tick a scrub drag emits —
 * there are usually far more of those than the decoder can seek through, so
 * firing one per tick queues up a backlog the displayed frame visibly lags
 * behind. Collapses that backlog into just the latest target once whatever
 * seek is already in flight lands.
 */
export const scheduleThumbnailSeek = (
  video: HTMLVideoElement,
  time: number,
  pendingTimeRef: React.MutableRefObject<number | null>,
): void => {
  pendingTimeRef.current = time

  const applyPendingSeek = (): void => {
    const next = pendingTimeRef.current
    pendingTimeRef.current = null
    if (next === null || next === video.currentTime) return

    video.addEventListener('seeked', applyPendingSeek, { once: true })
    video.currentTime = next
  }

  // Already mid-seek — whatever started it (this function or the commit's
  // own authoritative seek) will fire `seeked` on its own, but that listener
  // only knows about its own target. Attach ours too so this tick's request
  // still gets applied once the video settles, even if no further tick
  // arrives to retry it.
  if (video.seeking) {
    video.addEventListener('seeked', applyPendingSeek, { once: true })
    return
  }

  applyPendingSeek()
}

const getThumbnailSeekTime = (
  durationMs: number,
  sliderValue: number,
): number | null => {
  const time = (durationMs / 1000) * (sliderValue / RANGE_TOTAL_MAX)
  return Number.isFinite(time) ? Math.round(time * 1000) / 1000 : null
}

const formatVideoTimestamp = (seconds: number): string => {
  const totalSeconds = Math.round(seconds)
  const minutes = Math.floor(totalSeconds / 60)
  const remainingSeconds = totalSeconds % 60
  return `${minutes}:${String(remainingSeconds).padStart(2, '0')}`
}

// Derived the same way as the slider's own displayed value at its max
// position (RANGE_ALLOWABLE_MAX_LIMIT of RANGE_TOTAL_MAX — see
// VideoThumbnailSlider for why frames near the very end of a video are
// excluded), so dragging to the end and typing the same second agree.
const getMaxThumbnailSeconds = (durationMs: number): number => {
  const maxTime = getThumbnailSeekTime(durationMs, RANGE_ALLOWABLE_MAX_LIMIT)
  return maxTime === null ? 0 : Math.round(maxTime)
}

const getThumbnailSliderValueForSeconds = (
  durationMs: number,
  seconds: number,
): number =>
  Math.min(
    RANGE_ALLOWABLE_MAX_LIMIT,
    Math.max(0, Math.round(((seconds * 1000) / durationMs) * RANGE_TOTAL_MAX)),
  )

/**
 * The slider position for a video item: its staged (not yet applied)
 * thumbnail selection when one exists, otherwise the video's own committed
 * offset — so switching away from and back to a video the user already
 * scrubbed doesn't visually revert the still-staged edit.
 */
const getThumbnailSliderValueForVideoItem = (
  item: ImageWorkspaceVideoItem | null,
): number => {
  const durationMs = item?.originalVideo.durationMs
  if (!durationMs) return 0

  const offsetMs = Number(
    item?.thumbnailSelection?.offsetMs ?? item?.originalVideo.thumbOffset ?? 0,
  )
  return Math.min(
    RANGE_ALLOWABLE_MAX_LIMIT,
    Math.round((offsetMs / durationMs) * RANGE_TOTAL_MAX),
  )
}

const ComposerImageWorkspaceContent = React.forwardRef<
  ComposerImageWorkspaceContentHandle,
  ComposerImageWorkspaceContentProps
>(function ComposerImageWorkspaceContent(
  {
    images,
    videos,
    initialMedia,
    initialTask,
    validationIssues,
    sharedRatiosAreLimited = false,
    aspectRatios,
    showFreeAspectRatio = false,
    freeAspectRatioRange,
    altText,
    tagging,
    videoThumbnailEditing,
    taggingRef,
    status,
    trackingSession,
    onCloseButtonClick,
    onRequestImageSource,
    onRequestTaggingSignup,
    onActiveMediaChange,
    onApply,
    onCancel,
  },
  forwardedRef,
): JSX.Element {
  const [session, dispatch] = React.useReducer(
    imageWorkspaceSessionReducer,
    {
      images: images.map(({ image, editorSource }) => ({
        image,
        sourceDimensions: editorSource
          ? { width: editorSource.width, height: editorSource.height }
          : null,
      })),
      videos: videos?.map((item) => item.video) ?? [],
      initialMedia,
    },
    createImageWorkspaceSession,
  )
  const activeVideoIndex = getActiveVideoIndex(session)
  const activeVideoItem =
    activeVideoIndex !== null ? getActiveVideoWorkspaceItem(session) : null
  const activeVideo = activeVideoItem?.originalVideo ?? null
  // `videos` is a fresh array/wrapper on every render (the caller doesn't
  // memoize it), but the session's own video items must stay put unless a
  // video was genuinely added, removed, or swapped — keyed on the joined
  // URLs rather than the `videos` array reference so this only re-dispatches
  // when the actual attached set changes.
  const videoUrlsKey = videos?.map(({ video }) => video.url).join('|') ?? ''
  React.useEffect(() => {
    dispatch({
      type: 'sync-videos',
      videos: videos?.map((item) => item.video) ?? [],
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on videoUrlsKey, not the unmemoized `videos` array, so this only re-runs when the attached set actually changes
  }, [videoUrlsKey])
  const [selectedAspectRatios, setSelectedAspectRatios] = React.useState<
    Array<number | null>
  >(() => images.map(() => null))
  const [editorDraftValidity, setEditorDraftValidity] =
    React.useState<EditorDraftValidity | null>(null)
  const [taggingInteractionState, setTaggingInteractionState] =
    React.useState<ComposerImageUserTaggingInteractionState>({
      isPlacementActive: false,
      isEnteringUsername: false,
    })
  const [discardConfirmationOpen, setDiscardConfirmationOpen] =
    React.useState(false)
  const [filmstripFocusRequest, setFilmstripFocusRequest] =
    React.useState<FilmstripFocusRequest>()
  const [pendingImage, setPendingImage] = React.useState<Image | null>(null)
  const [sourceFailureImage, setSourceFailureImage] =
    React.useState<Image | null>(null)
  const thumbnailVideoRef = React.useRef<HTMLVideoElement>(null)
  const thumbnailCommitTokenRef = React.useRef(0)
  const pendingThumbnailSeekTimeRef = React.useRef<number | null>(null)
  const [pendingThumbnailCaptureCount, setPendingThumbnailCaptureCount] =
    React.useState(0)
  const [thumbnailCaptureWarning, setThumbnailCaptureWarning] = React.useState<
    string | null
  >(null)
  const videoTitleInputId = React.useId()
  const videoTimestampInputId = React.useId()
  const [thumbnailSliderValue, setThumbnailSliderValue] = React.useState(
    (): number => getThumbnailSliderValueForVideoItem(activeVideoItem),
  )
  // Raw text the user is currently typing into the timestamp input; null
  // once they commit, so the field displays the slider-derived value again.
  const [thumbnailSecondsDraft, setThumbnailSecondsDraft] = React.useState<
    string | null
  >(null)
  // Switching the active video (via the filmstrip) must reset the thumbnail
  // scrubber to that video's own state — its staged selection if the user
  // already scrubbed it, otherwise its committed offset — and invalidate any
  // capture still in flight for the video navigated away from. Keyed on the
  // video's own identity, not the item, so a later edit to the same video's
  // staged thumbnail doesn't re-trigger this reset. A `useLayoutEffect`
  // applies this before paint so there's no stale flash of the previous
  // video's slider position.
  React.useLayoutEffect(() => {
    thumbnailCommitTokenRef.current += 1
    pendingThumbnailSeekTimeRef.current = null
    setThumbnailCaptureWarning(null)
    setThumbnailSecondsDraft(null)
    setThumbnailSliderValue(
      getThumbnailSliderValueForVideoItem(activeVideoItem),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on activeVideo (identity), reads the current activeVideoItem without reacting to its own later edits
  }, [activeVideo])
  const videoIsActive = isImageWorkspaceVideoActive(session)
  // Alt text and tagging never apply to video, so their tabs disappear
  // entirely while it's active rather than showing disabled controls; edit
  // gives way to the thumbnail tool, the only editing video supports.
  const availableTasks = getAvailableTasks(
    videoIsActive,
    videoThumbnailEditing !== undefined,
    altText !== undefined && !videoIsActive,
    (tagging !== undefined || onRequestTaggingSignup !== undefined) &&
      !videoIsActive,
  )
  const requestedInitialTask =
    initialTask ?? (tagging?.initialMode ? 'tagging' : 'edit')
  const [activeTask, setActiveTask] =
    React.useState<ComposerImageWorkspaceTask>(() =>
      availableTasks.includes(requestedInitialTask)
        ? requestedInitialTask
        : (availableTasks[0] ?? 'edit'),
    )
  const activeTaskIsAvailable = availableTasks.includes(activeTask)
  React.useEffect(() => {
    if (!activeTaskIsAvailable) setActiveTask(availableTasks[0] ?? 'edit')
  }, [activeTaskIsAvailable, availableTasks])
  const discardReturnFocusRef = React.useRef<HTMLElement | null>(null)
  const pendingDismissMethodRef =
    React.useRef<ImageWorkspaceDismissMethod | null>(null)
  const altTextRef = React.useRef<HTMLTextAreaElement>(null)
  const taskSelectorRef = React.useRef<HTMLFieldSetElement>(null)
  const taggingSignupTriggerRef = React.useRef<HTMLButtonElement>(null)
  const readyCloseButtonRef = React.useRef<HTMLButtonElement>(null)
  const sourceRetryFocusImageRef = React.useRef<Image | null>(null)
  const sourceRequestRef = React.useRef<
    Readonly<{ image: Image; key: number }> | undefined
  >()
  const sourceRequestKeyRef = React.useRef(0)
  const initialTaggingModeHandledRef = React.useRef(false)
  const applyDisabledReasonId = React.useId()
  const editorPanelId = React.useId()

  React.useEffect(
    (): (() => void) => () => {
      sourceRequestKeyRef.current += 1
      sourceRequestRef.current = undefined
    },
    [],
  )

  React.useEffect(() => {
    for (const workspaceImage of images) {
      const { width, height } = workspaceImage.editorSource ?? {}
      if (
        typeof width !== 'number' ||
        width <= 0 ||
        typeof height !== 'number' ||
        height <= 0
      ) {
        continue
      }

      dispatch({
        type: 'set-source-dimensions',
        image: workspaceImage.image,
        sourceDimensions: { width, height },
      })
    }
  }, [images])

  // While the video is active, `activeItem`/`activeImage`/`workspaceImage`
  // fall back to the last image so the (otherwise unused) per-image
  // derivations below stay well-defined; the video stage renders instead of
  // the image editor surface, so none of these fallback values reach the
  // screen. A video-only draft has no image to fall back to, so all three
  // stay null and every downstream image-only branch is skipped.
  const activeItemIndex = videoIsActive
    ? session.items.length - 1
    : session.activeIndex
  const activeItem =
    activeItemIndex >= 0
      ? getImageWorkspaceItemAt(session, activeItemIndex)
      : null
  const activeImage = activeItem?.originalImage ?? null
  const workspaceImage =
    activeItem && activeImage
      ? getWorkspaceImageAtIndex(images, activeImage, activeItemIndex)
      : null
  const preparedSource = workspaceImage?.editorSource
  const { isPlacementActive: isTagPlacementActive, isEnteringUsername } =
    taggingInteractionState
  const editorHasBeenReady =
    videoIsActive ||
    (preparedSource !== undefined &&
      editorDraftValidity?.sourceSrc === preparedSource.src &&
      editorDraftValidity.activeIndex === session.activeIndex &&
      editorDraftValidity.hasBeenReady)
  // No editor surface renders while the video is active, so there is
  // nothing for it to invalidate — Apply must stay gated only by the
  // session's own dirty/blocked state in that case.
  const editorDraftIsValid =
    videoIsActive ||
    (preparedSource !== undefined &&
      editorDraftValidity !== null &&
      editorDraftValidity.sourceSrc === preparedSource.src &&
      editorDraftValidity.activeIndex === session.activeIndex &&
      editorDraftValidity.isValid)
  const editorIssue =
    preparedSource !== undefined &&
    editorDraftValidity?.sourceSrc === preparedSource.src &&
    editorDraftValidity.activeIndex === session.activeIndex
      ? editorDraftValidity.issue
      : null
  const isApplying = status.state === 'applying'
  const sessionIsDirty = isImageWorkspaceSessionDirty(session)
  const changeSummary = React.useMemo(
    () => getImageWorkspaceChangeSummary(session),
    [session],
  )
  const dirtyImageCount = session.items.filter(isImageWorkspaceItemDirty).length
  const dirtyItemCount = dirtyImageCount + getDirtyVideoCount(session)
  const activeFilmstripTabId = getImageWorkspaceFilmstripTabId(
    editorPanelId,
    session.activeIndex,
  )
  const pendingImageIndex = pendingImage
    ? session.items.findIndex((item) => item.originalImage === pendingImage)
    : -1
  const sourceFailureImageIndex = sourceFailureImage
    ? session.items.findIndex(
        (item) => item.originalImage === sourceFailureImage,
      )
    : -1
  const sourceRetryIsPending =
    sourceFailureImage !== null && pendingImage === sourceFailureImage
  const selectedAspectRatio = selectedAspectRatios[session.activeIndex] ?? null
  const totalTagCount = getImageWorkspaceStagedUserTagCount(session)
  const userTagProjections = session.items.map(
    getImageWorkspaceUserTagProjections,
  )
  const activeUserTags = userTagProjections[session.activeIndex] ?? []
  const activeOutsideCropTagCount = activeUserTags.filter(
    (tag) => tag.state === 'outside',
  ).length
  const workspaceResult = getImageWorkspaceResult(session)
  const activeImageHasBlockingInvalidTags =
    workspaceResult.state === 'blocked' &&
    workspaceResult.imageIndexes.includes(session.activeIndex)
  const inactiveBlockingImageIndexes =
    workspaceResult.state === 'blocked'
      ? workspaceResult.imageIndexes.filter(
          (imageIndex) => imageIndex !== session.activeIndex,
        )
      : []
  const inactiveOutsideCropEntries = userTagProjections.flatMap(
    (projections, imageIndex) => {
      if (imageIndex === session.activeIndex) return []

      const tagCount = projections.filter(
        (tag) => tag.state === 'outside',
      ).length

      return tagCount > 0 ? [{ imageIndex, tagCount }] : []
    },
  )
  const filmstripItems: readonly ComposerImageWorkspaceFilmstripItem[] =
    session.items.map((item, imageIndex) => {
      const filmstripImage = getWorkspaceImageAtIndex(
        images,
        item.originalImage,
        imageIndex,
      )
      const outsideCropTagCount = (userTagProjections[imageIndex] ?? []).filter(
        (tag) => tag.state === 'outside',
      ).length
      const hasBlockingInvalidTags =
        workspaceResult.state === 'blocked' &&
        workspaceResult.imageIndexes.includes(imageIndex)
      const validationIssue = validationIssues?.find(
        (candidate) => candidate.image === item.originalImage,
      )
      const issue: ComposerImageWorkspaceFilmstripIssue | undefined =
        hasBlockingInvalidTags
          ? { severity: 'error', label: 'Tag position needs attention' }
          : filmstripImage.sourceStatus === 'error'
            ? { severity: 'error', label: 'Image could not be prepared' }
            : validationIssue
              ? { severity: 'error', label: validationIssue.message }
              : outsideCropTagCount > 0
                ? {
                    severity: 'warning',
                    label: `${outsideCropTagCount} outside crop`,
                  }
                : undefined

      return {
        kind: 'image' as const,
        image: item.originalImage,
        thumbnailSrc:
          filmstripImage.editorSource?.src ?? item.originalImage.url,
        thumbnailWidth:
          item.sourceDimensions?.width ?? item.originalImage.width ?? 1,
        thumbnailHeight:
          item.sourceDimensions?.height ?? item.originalImage.height ?? 1,
        edit: item.edit,
        hasStagedChanges: isImageWorkspaceItemDirty(item),
        tagCount: item.userTagAnchors.length,
        issue,
      }
    })
  const filmstripItemsWithVideo: readonly ComposerImageWorkspaceFilmstripItem[] =
    videos && videos.length > 0
      ? [
          ...filmstripItems,
          ...videos.map(
            ({ video }): ComposerImageWorkspaceFilmstripItem => ({
              kind: 'video' as const,
              video,
              thumbnailSrc: video.thumbnail ?? null,
            }),
          ),
        ]
      : filmstripItems

  React.useEffect(() => {
    trackingSession?.workspaceReady(
      getTrackingTask(activeTask),
      session.activeIndex + 1,
    )
  }, [activeTask, session.activeIndex, trackingSession])

  React.useEffect(() => {
    trackingSession?.snapshotUpdated(changeSummary)
  }, [changeSummary, trackingSession])

  React.useEffect(() => {
    const initialMode = tagging?.initialMode
    if (
      activeTask !== 'tagging' ||
      !initialMode ||
      initialTaggingModeHandledRef.current
    ) {
      return
    }

    if (activeImage !== initialMedia) {
      initialTaggingModeHandledRef.current = true
      return
    }

    if (!editorHasBeenReady) return

    const taggingHandle = taggingRef.current
    if (!taggingHandle) return

    taggingHandle.revealSection(
      initialMode === 'manage' ? { onlyIfFocused: true } : undefined,
    )
    if (initialMode === 'manage') {
      initialTaggingModeHandledRef.current = true
      return
    }

    if (isApplying) return

    taggingHandle.focusSection()
    if (
      taggingHandle.startPlacement() ||
      totalTagCount >= tagging.maxTagCount
    ) {
      initialTaggingModeHandledRef.current = true
    }
  }, [
    activeTask,
    activeImage,
    editorHasBeenReady,
    initialMedia,
    isApplying,
    tagging?.maxTagCount,
    tagging?.initialMode,
    taggingRef,
    totalTagCount,
  ])

  const requestDismiss = React.useCallback(
    (method: ImageWorkspaceDismissMethod): void => {
      if (sessionIsDirty || pendingThumbnailCaptureCount > 0) {
        pendingDismissMethodRef.current = method
        discardReturnFocusRef.current =
          document.activeElement instanceof HTMLElement
            ? document.activeElement
            : null
        setDiscardConfirmationOpen(true)
        return
      }

      trackingSession?.close(
        {
          closeReason: 'cancelled',
          closeMethod: method,
        },
        changeSummary,
      )
      onCancel()
    },
    [
      changeSummary,
      sessionIsDirty,
      pendingThumbnailCaptureCount,
      onCancel,
      trackingSession,
    ],
  )

  React.useImperativeHandle(
    forwardedRef,
    () => ({
      focusInitialTask: (): boolean => {
        if (activeTask === 'alt-text') {
          const textarea = altTextRef.current
          if (!textarea) return false

          textarea.focus()
          return true
        }

        if (activeTask === 'tagging') {
          const taggingHandle = taggingRef.current
          if (!taggingHandle) return false

          taggingHandle.focusSection()
          return true
        }

        const selectedTask =
          taskSelectorRef.current?.querySelector<HTMLElement>(
            '[role="radio"][aria-checked="true"]',
          )
        if (!selectedTask) {
          const closeButton = readyCloseButtonRef.current
          if (!closeButton) return false

          closeButton.focus()
          return true
        }

        selectedTask.focus()
        return true
      },
      requestDismiss,
    }),
    [activeTask, requestDismiss, taggingRef],
  )

  const setSelectedAspectRatio = (value: number | null): void => {
    setSelectedAspectRatios((currentValues) => {
      return currentValues.map((currentValue, index) =>
        index === session.activeIndex ? value : currentValue,
      )
    })
  }

  const resetActiveImageEdit = (): void => {
    if (!activeImage) return

    dispatch({
      type: 'set-edit',
      image: activeImage,
      edit: createIdentityImageEdit(),
    })
    setSelectedAspectRatio(null)
  }

  const selectVideo = (nextVideo: Video): void => {
    sourceRequestKeyRef.current += 1
    sourceRequestRef.current = undefined

    const targetVideoIndex = session.videos.findIndex(
      (item) => item.originalVideo === nextVideo,
    )
    if (targetVideoIndex === -1 || targetVideoIndex === activeVideoIndex) {
      return
    }

    taggingRef.current?.cancelPlacement(null)
    setTaggingInteractionState({
      isPlacementActive: false,
      isEnteringUsername: false,
    })
    setPendingImage(null)
    setSourceFailureImage(null)
    sourceRetryFocusImageRef.current = null
    dispatch({ type: 'select-video', video: nextVideo })
    onActiveMediaChange?.(nextVideo)
    trackingSession?.imageChanged(session.items.length + targetVideoIndex + 1)
  }

  const selectImage = (image: Image): void => {
    sourceRequestKeyRef.current += 1
    sourceRequestRef.current = undefined

    if (!videoIsActive && image === activeImage) {
      setPendingImage(null)
      setSourceFailureImage(null)
      sourceRetryFocusImageRef.current = null
      return
    }

    const imageIndex = session.items.findIndex(
      (item) => item.originalImage === image,
    )
    if (imageIndex < 0) return

    taggingRef.current?.cancelPlacement(null)
    setTaggingInteractionState({
      isPlacementActive: false,
      isEnteringUsername: false,
    })
    if (sourceFailureImage !== image) {
      setSourceFailureImage(null)
      sourceRetryFocusImageRef.current = null
    }
    const nextWorkspaceImage = getWorkspaceImageAtIndex(
      images,
      image,
      imageIndex,
    )
    if (
      !isWorkspaceImageReady(nextWorkspaceImage) &&
      activeTask !== 'alt-text'
    ) {
      setPendingImage(image)
      const request = onRequestImageSource?.(image)
      if (request) {
        sourceRequestKeyRef.current += 1
        const requestKey = sourceRequestKeyRef.current
        sourceRequestRef.current = { image, key: requestKey }
        const handleSourceRequest = async (): Promise<void> => {
          const sourceReady = await request
          if (sourceRequestRef.current?.key !== requestKey) return

          if (sourceReady) return

          sourceRequestRef.current = undefined
          setPendingImage((currentImage) =>
            currentImage === image ? null : currentImage,
          )
          setSourceFailureImage(image)
        }
        void handleSourceRequest()
      }
      return
    }

    setPendingImage(null)
    setSourceFailureImage(null)
    sourceRetryFocusImageRef.current = null
    dispatch({ type: 'select-image', image })
    onActiveMediaChange?.(image)
    trackingSession?.imageChanged(imageIndex + 1)
    if (!isWorkspaceImageReady(nextWorkspaceImage)) {
      void onRequestImageSource?.(image)
    }
  }

  React.useEffect(() => {
    if (!pendingImage) return

    const imageIndex = session.items.findIndex(
      (item) => item.originalImage === pendingImage,
    )
    if (imageIndex < 0) {
      setPendingImage(null)
      return
    }

    const pendingWorkspaceImage = getWorkspaceImageAtIndex(
      images,
      pendingImage,
      imageIndex,
    )
    if (pendingWorkspaceImage.sourceStatus === 'error') {
      if (sourceRequestRef.current?.image === pendingImage) return

      setSourceFailureImage(pendingImage)
      setPendingImage(null)
      return
    }
    if (!isWorkspaceImageReady(pendingWorkspaceImage)) return

    const shouldRestoreRetryFocus =
      sourceRetryFocusImageRef.current === pendingImage
    if (sourceRequestRef.current?.image === pendingImage) {
      sourceRequestRef.current = undefined
    }
    taggingRef.current?.cancelPlacement(null)
    setPendingImage(null)
    setSourceFailureImage(null)
    sourceRetryFocusImageRef.current = null
    dispatch({ type: 'select-image', image: pendingImage })
    onActiveMediaChange?.(pendingImage)
    trackingSession?.imageChanged(imageIndex + 1)
    if (shouldRestoreRetryFocus) {
      setFilmstripFocusRequest((currentRequest) => ({
        index: imageIndex,
        key: (currentRequest?.key ?? 0) + 1,
      }))
    }
  }, [
    images,
    onActiveMediaChange,
    pendingImage,
    session.items,
    taggingRef,
    trackingSession,
  ])

  const selectItemAtIndex = (itemIndex: number): void => {
    if (itemIndex >= session.items.length) {
      const videoItem = session.videos[itemIndex - session.items.length]
      if (videoItem) selectVideo(videoItem.originalVideo)
      return
    }

    const item = session.items[itemIndex]
    if (!item) return

    selectImage(item.originalImage)
  }

  const handleTaskChange = (task: string): void => {
    if (
      task === '' ||
      task === activeTask ||
      !isComposerImageWorkspaceTask(task) ||
      !availableTasks.includes(task)
    ) {
      return
    }

    if (task === 'tagging' && onRequestTaggingSignup) {
      const trigger = taggingSignupTriggerRef.current
      if (trigger) onRequestTaggingSignup(trigger)
      return
    }

    if (activeTask === 'tagging') {
      taggingRef.current?.cancelPlacement(null)
      setTaggingInteractionState({
        isPlacementActive: false,
        isEnteringUsername: false,
      })
    }

    setActiveTask(task)
    trackingSession?.taskChanged(getTrackingTask(task))
  }

  const reviewImage = (imageIndex: number): void => {
    const item = session.items[imageIndex]
    if (!item) return

    selectImage(item.originalImage)
    setFilmstripFocusRequest((currentRequest) => ({
      index: imageIndex,
      key: (currentRequest?.key ?? 0) + 1,
    }))
  }

  const renderTaskControls = (
    taggingControls?: React.ReactNode,
  ): React.ReactNode => {
    if (!altText && !taggingControls) return undefined

    return (
      <>
        {taggingControls ? (
          <div
            className={styles.metadataControls}
            hidden={activeTask !== 'tagging'}
          >
            <div>{taggingControls}</div>
          </div>
        ) : null}
        {altText && activeImage && activeItem ? (
          <div
            className={styles.metadataControls}
            hidden={activeTask !== 'alt-text'}
          >
            <ComposerImageAltText
              ref={altTextRef}
              imageUrl={activeImage.url}
              value={activeItem.altText}
              maxLength={altText.maxLength}
              onRequestSignup={altText.onRequestSignup}
              disabled={isApplying}
              generationDisabledReason={
                altText.onRequestSignup || isIdentityImageEdit(activeItem.edit)
                  ? undefined
                  : APPLY_VISUAL_EDITS_BEFORE_GENERATING_ALT_TEXT
              }
              compact
              onValueChange={(value) =>
                dispatch({
                  type: 'set-alt-text',
                  image: activeImage,
                  altText: value,
                })
              }
            />
          </div>
        ) : null}
      </>
    )
  }

  const imageNavigationDisabled =
    isApplying || isEnteringUsername || pendingImage !== null
  const previousImageIndex = session.activeIndex - 1
  const nextImageIndex = session.activeIndex + 1
  const previousImageDisabled =
    imageNavigationDisabled || previousImageIndex < 0
  const nextImageDisabled =
    imageNavigationDisabled ||
    nextImageIndex >= getImageWorkspaceItemCount(session)
  const stageWarning: WorkspaceStageWarning | null =
    activeOutsideCropTagCount > 0
      ? {
          message: `${activeOutsideCropTagCount} ${activeOutsideCropTagCount === 1 ? 'tag is' : 'tags are'} outside the crop and won’t be included when you apply changes. Reset crop & transform to keep ${activeOutsideCropTagCount === 1 ? 'it' : 'them'}.`,
        }
      : inactiveOutsideCropEntries.length > 0
        ? {
            message: getInactiveOutsideCropWarning(inactiveOutsideCropEntries),
            reviewImageIndex: inactiveOutsideCropEntries[0].imageIndex,
          }
        : null
  const stageNotice = stageWarning ? (
    <Notice variant="warning" aria-label={stageWarning.message}>
      <Notice.Text>{stageWarning.message}</Notice.Text>
      {stageWarning.reviewImageIndex !== undefined ? (
        <Notice.Actions>
          <Button
            size="small"
            disabled={isApplying || isEnteringUsername}
            onClick={() => {
              if (stageWarning.reviewImageIndex !== undefined) {
                reviewImage(stageWarning.reviewImageIndex)
              }
            }}
          >
            Review Image {stageWarning.reviewImageIndex + 1}
          </Button>
        </Notice.Actions>
      ) : null}
    </Notice>
  ) : undefined
  const stageActions =
    getImageWorkspaceItemCount(session) > 1 ? (
      <div className={styles.stageActions}>
        <IconButton
          className={styles.stageActionButton}
          label="Previous item"
          tooltip="Previous item"
          variant="tertiary"
          size="large"
          aria-controls={editorPanelId}
          aria-disabled={previousImageDisabled}
          onClick={() => {
            if (!previousImageDisabled) selectItemAtIndex(previousImageIndex)
          }}
        >
          <ChevronLeftIcon />
        </IconButton>
        <IconButton
          className={styles.stageActionButton}
          label="Next item"
          tooltip="Next item"
          variant="tertiary"
          size="large"
          aria-controls={editorPanelId}
          aria-disabled={nextImageDisabled}
          onClick={() => {
            if (!nextImageDisabled) selectItemAtIndex(nextImageIndex)
          }}
        >
          <ChevronRightIcon />
        </IconButton>
      </div>
    ) : undefined
  const taskSelector =
    availableTasks.length > 1 ? (
      <fieldset ref={taskSelectorRef} className={styles.taskSelector}>
        <VisuallyHidden as="legend">Image editing task</VisuallyHidden>
        <ToggleGroup
          className={styles.taskToggleGroup}
          value={activeTask}
          disabled={isApplying || isEnteringUsername}
          onChange={handleTaskChange}
        >
          {availableTasks.map((task) => (
            <ToggleGroup.Item
              key={task}
              ref={task === 'tagging' ? taggingSignupTriggerRef : undefined}
              value={task}
              className={styles.taskToggleItem}
            >
              {TASK_LABELS[task]}
            </ToggleGroup.Item>
          ))}
        </ToggleGroup>
      </fieldset>
    ) : undefined
  const adjustmentsHeader =
    taskSelector || sharedRatiosAreLimited ? (
      <div className={styles.adjustmentsHeader}>
        {taskSelector}
        {sharedRatiosAreLimited ? (
          <Text as="p" size="sm" color="subtle">
            Only ratios supported by every selected network are shown. To use
            each network&apos;s full set of ratios, close the editor and
            customize each network separately.
          </Text>
        ) : null}
      </div>
    ) : undefined
  const imageSelector = (
    <div className={styles.imageSelector}>
      <ComposerImageWorkspaceFilmstrip
        items={filmstripItemsWithVideo}
        activeIndex={session.activeIndex}
        panelId={editorPanelId}
        disabled={isApplying || isEnteringUsername}
        focusRequest={filmstripFocusRequest}
        onSelect={(media) =>
          isVideoMedia(media) ? selectVideo(media) : selectImage(media)
        }
      />
    </div>
  )

  const renderEditorSurface = (
    taggingControls?: React.ReactNode,
    imageOverlay?: React.ReactNode,
    alternateInteraction?: ImageEditorSurfaceAlternateInteraction,
  ): JSX.Element => {
    if (!activeItem || !activeImage || !workspaceImage) {
      throw new Error('The image editor surface requires an active image')
    }

    return (
      <ImageEditorSurface
        className={styles.editorSurface}
        mode={activeTask === 'edit' ? 'edit' : 'preview'}
        source={
          preparedSource ?? {
            src: activeImage.url,
            crossOrigin: null,
            width: activeImage.width,
            height: activeImage.height,
          }
        }
        value={activeItem.edit}
        selectedAspectRatio={selectedAspectRatio}
        aspectRatios={aspectRatios}
        showFreeAspectRatio={showFreeAspectRatio}
        freeAspectRatioRange={freeAspectRatioRange}
        disabled={isApplying || preparedSource === undefined}
        adjustmentsHeader={adjustmentsHeader}
        showInlineNotices={false}
        onValidityChange={(isValid, issue) =>
          setEditorDraftValidity((currentValidity) => ({
            activeIndex: session.activeIndex,
            sourceSrc: preparedSource?.src,
            errorType: issue
              ? currentValidity?.activeIndex === session.activeIndex &&
                currentValidity.sourceSrc === preparedSource?.src &&
                currentValidity.errorType === 'imageLoadFailed'
                ? 'imageLoadFailed'
                : 'outputSizeInvalid'
              : null,
            hasBeenReady:
              isValid ||
              (currentValidity?.activeIndex === session.activeIndex &&
                currentValidity.sourceSrc === preparedSource?.src &&
                currentValidity.hasBeenReady),
            isValid,
            issue: issue ?? null,
          }))
        }
        onSourceLoadError={() =>
          setEditorDraftValidity({
            activeIndex: session.activeIndex,
            sourceSrc: preparedSource?.src,
            errorType: 'imageLoadFailed',
            hasBeenReady: false,
            isValid: false,
            issue: 'This image could not be loaded.',
          })
        }
        onValueChange={(edit) =>
          dispatch({ type: 'set-edit', image: activeImage, edit })
        }
        onSelectedAspectRatioChange={setSelectedAspectRatio}
        cropControls={
          activeTask === 'edit' ? (
            <Button
              variant="secondary"
              size="medium"
              disabled={
                (isIdentityImageEdit(activeItem.edit) &&
                  selectedAspectRatio === null) ||
                isApplying ||
                isTagPlacementActive
              }
              onClick={resetActiveImageEdit}
            >
              Reset
              <VisuallyHidden> image edits</VisuallyHidden>
            </Button>
          ) : undefined
        }
        additionalControls={renderTaskControls(taggingControls)}
        additionalControlsHidden={activeTask === 'edit'}
        imageOverlay={
          (activeTask === 'edit' || activeTask === 'tagging') &&
          workspaceImage.sourceStatus === 'loading' ? (
            <output className={styles.preparationOverlay}>
              <SimpleSpinner size="large" aria-hidden="true" />
              <Text>Preparing editing tools…</Text>
            </output>
          ) : (
            imageOverlay
          )
        }
        alternateInteraction={alternateInteraction}
        stageNotice={stageNotice}
        stageActions={stageActions}
        stageFooter={imageSelector}
      />
    )
  }

  const commitThumbnailTime = (time: number): void => {
    const videoEl = thumbnailVideoRef.current
    if (!videoEl || !activeVideo) return

    // A scrub tick's own seek can still be in flight when this fires (e.g.
    // the slider's commit callback runs before that seek settles). Drop it
    // so its `applyPendingSeek` has nothing left to apply once `seeked`
    // lands, and can't re-seek away from the time captured below.
    pendingThumbnailSeekTimeRef.current = null

    const targetVideo = activeVideo
    const commitToken = ++thumbnailCommitTokenRef.current
    setPendingThumbnailCaptureCount((count) => count + 1)
    setThumbnailCaptureWarning(null)
    void seekAndCaptureVideoFrame(videoEl, time).then((result) => {
      setPendingThumbnailCaptureCount((count) => count - 1)
      if (commitToken !== thumbnailCommitTokenRef.current) return undefined

      if (!result.ok) {
        setThumbnailCaptureWarning(THUMBNAIL_SEEK_FAILED_WARNING)
        return undefined
      }

      return dispatch({
        type: 'set-video-thumbnail-selection',
        video: targetVideo,
        selection: { offsetMs: Math.round(time * 1000), blob: result.blob },
      })
    })
  }

  const handleThumbnailSliderChange = (value: number): void => {
    setThumbnailSliderValue(value)
    setThumbnailSecondsDraft(null)

    const videoEl = thumbnailVideoRef.current
    const durationMs = activeVideo?.durationMs
    const time = durationMs ? getThumbnailSeekTime(durationMs, value) : null
    if (videoEl && time !== null) {
      scheduleThumbnailSeek(videoEl, time, pendingThumbnailSeekTimeRef)
    }
  }

  const handleThumbnailSliderCommit = (value: number): void => {
    const durationMs = activeVideo?.durationMs
    const time = durationMs ? getThumbnailSeekTime(durationMs, value) : null
    if (time === null) return

    commitThumbnailTime(time)
  }

  const handleThumbnailSecondsCommit = (
    durationMs: number,
    rawValue: string,
  ): void => {
    setThumbnailSecondsDraft(null)

    const parsedSeconds = Math.round(Number.parseFloat(rawValue))
    if (!Number.isFinite(parsedSeconds)) return

    const clampedSeconds = Math.min(
      getMaxThumbnailSeconds(durationMs),
      Math.max(0, parsedSeconds),
    )
    const sliderValue = getThumbnailSliderValueForSeconds(
      durationMs,
      clampedSeconds,
    )
    setThumbnailSliderValue(sliderValue)

    const time = getThumbnailSeekTime(durationMs, sliderValue)
    if (time === null) return

    const videoEl = thumbnailVideoRef.current
    if (videoEl) videoEl.currentTime = time
    commitThumbnailTime(time)
  }

  const renderVideoStage = (video: Video): JSX.Element => {
    const durationMs = video.durationMs
    const thumbnailSeekTime = durationMs
      ? getThumbnailSeekTime(durationMs, thumbnailSliderValue)
      : null
    const thumbnailSeconds =
      thumbnailSeekTime !== null ? Math.round(thumbnailSeekTime) : 0
    const thumbnailTimestampLabel =
      durationMs && thumbnailSeekTime !== null
        ? `${formatVideoTimestamp(thumbnailSeekTime)} of ${formatVideoTimestamp(durationMs / 1000)}`
        : undefined

    return (
      <div className={styles.videoStage}>
        <div className={styles.videoStagePanel}>
          <div className={styles.videoStageViewport}>
            {/* oxlint-disable-next-line jsx-a11y/media-has-caption -- User uploads do not include caption tracks in the editor preview. */}
            <video
              key={video.url}
              ref={videoThumbnailEditing ? thumbnailVideoRef : undefined}
              aria-label="Video attachment preview"
              className={styles.videoStagePlayer}
              src={video.url}
              poster={video.thumbnail}
              preload="metadata"
              crossOrigin={videoThumbnailEditing ? 'anonymous' : undefined}
              controls
              playsInline
            />
            {stageActions}
          </div>
        </div>
        {imageSelector}
        {videoThumbnailEditing && (
          <div className={styles.videoAdjustmentsPanel}>
            {videoThumbnailEditing.canEditTitle && (
              <div className={styles.videoTitleField}>
                <Label htmlFor={videoTitleInputId}>Title</Label>
                <Input
                  id={videoTitleInputId}
                  value={activeVideoItem?.title ?? ''}
                  onChange={(event): void => {
                    if (!activeVideo) return
                    dispatch({
                      type: 'set-video-title',
                      video: activeVideo,
                      title: event.target.value,
                    })
                  }}
                />
              </div>
            )}
            <div className={styles.videoThumbnailControls}>
              <Slider
                label="Thumbnail frame"
                value={thumbnailSliderValue}
                min={0}
                max={RANGE_ALLOWABLE_MAX_LIMIT}
                onValueChange={handleThumbnailSliderChange}
                onValueCommit={handleThumbnailSliderCommit}
                aria-valuetext={thumbnailTimestampLabel}
              />
              {thumbnailCaptureWarning && (
                <Notice variant="warning" aria-label={thumbnailCaptureWarning}>
                  <Notice.Text>{thumbnailCaptureWarning}</Notice.Text>
                </Notice>
              )}
              <div className={styles.videoTimestampField}>
                <Label htmlFor={videoTimestampInputId}>
                  Thumbnail time (seconds)
                </Label>
                <Input
                  id={videoTimestampInputId}
                  className={styles.videoTimestampInput}
                  type="number"
                  inputMode="decimal"
                  min={0}
                  max={durationMs ? getMaxThumbnailSeconds(durationMs) : 0}
                  step={1}
                  value={thumbnailSecondsDraft ?? String(thumbnailSeconds)}
                  onChange={(event): void =>
                    setThumbnailSecondsDraft(event.target.value)
                  }
                  onBlur={(event): void => {
                    if (!durationMs) return
                    handleThumbnailSecondsCommit(durationMs, event.target.value)
                  }}
                  onKeyDown={(event): void => {
                    if (event.key === 'Enter') event.currentTarget.blur()
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  const editorSurface =
    videoIsActive && activeVideo ? (
      renderVideoStage(activeVideo)
    ) : tagging && activeItem && activeImage ? (
      <ComposerImageUserTagging
        ref={taggingRef}
        tags={activeUserTags}
        maxTagCount={tagging.maxTagCount}
        totalTagCount={totalTagCount}
        disabled={isApplying || activeTask !== 'tagging'}
        placementDisabled={!editorHasBeenReady || activeTask !== 'tagging'}
        onInteractionStateChange={setTaggingInteractionState}
        onAddTag={(tag) =>
          dispatch({
            type: 'add-user-tag',
            image: activeImage,
            tag,
          })
        }
        onMoveTag={(anchorIndex, point) =>
          dispatch({
            type: 'move-user-tag',
            image: activeImage,
            anchorIndex,
            point,
          })
        }
        onRemoveTag={(anchorIndex) =>
          dispatch({
            type: 'remove-user-tag',
            image: activeImage,
            anchorIndex,
          })
        }
      >
        {({
          imageOverlay,
          cropOverlay,
          stageOverlay,
          controls,
          isPlacementActive,
        }) =>
          renderEditorSurface(
            activeTask === 'tagging' ? controls : undefined,
            activeTask === 'edit' || activeTask === 'tagging'
              ? imageOverlay
              : undefined,
            activeTask === 'tagging' && isPlacementActive
              ? {
                  canvasLabel: 'Image tag placement area',
                  cropOverlay,
                  stageOverlay,
                }
              : undefined,
          )
        }
      </ComposerImageUserTagging>
    ) : (
      renderEditorSurface()
    )
  const applyDisabledReason =
    pendingThumbnailCaptureCount > 0
      ? 'Saving your thumbnail frame…'
      : isTagPlacementActive && !isApplying
        ? 'Finish or cancel tag placement first.'
        : undefined
  const applyIsDisabled =
    !sessionIsDirty ||
    (activeItem !== null &&
      !isIdentityImageEdit(activeItem.edit) &&
      !editorDraftIsValid) ||
    isApplying ||
    pendingImage !== null ||
    isTagPlacementActive ||
    pendingThumbnailCaptureCount > 0 ||
    workspaceResult.state === 'blocked'
  const applyUsesAriaDisabled = applyDisabledReason !== undefined
  const applyLabel =
    dirtyItemCount === 0
      ? 'Apply'
      : `Apply Changes to ${dirtyItemCount} ${dirtyItemCount === 1 ? 'Item' : 'Items'}`
  const applyButton = (
    <Button
      loading={isApplying}
      className={applyUsesAriaDisabled ? styles.disabledAction : undefined}
      disabled={applyIsDisabled && !applyUsesAriaDisabled}
      aria-disabled={applyUsesAriaDisabled ? true : undefined}
      aria-describedby={applyDisabledReason ? applyDisabledReasonId : undefined}
      onClick={() => {
        if (applyIsDisabled) return

        if (workspaceResult.state === 'ready') {
          trackingSession?.applyStarted(changeSummary)
          onApply(workspaceResult.result)
        }
      }}
    >
      {applyLabel}
    </Button>
  )
  const workspaceNotice: WorkspaceNotice | null = (() => {
    if (status.state === 'error') {
      return {
        variant: 'error',
        message: status.message,
      }
    }

    if (
      !videoIsActive &&
      workspaceImage?.sourceStatus === 'error' &&
      activeImage
    ) {
      return {
        variant: 'error',
        message: `Image ${session.activeIndex + 1} could not be prepared for editing. You can still save alt text.`,
        action: (
          <Button
            size="small"
            disabled={isApplying}
            onClick={() => void onRequestImageSource?.(activeImage)}
          >
            Try Again
          </Button>
        ),
      }
    }

    if (editorIssue) {
      return {
        variant: 'error',
        message: editorIssue,
      }
    }

    if (sourceFailureImageIndex >= 0 && sourceFailureImage) {
      return {
        variant: 'error',
        message: `Image ${sourceFailureImageIndex + 1} could not be prepared.`,
        action: (
          <Button
            size="small"
            className={sourceRetryIsPending ? styles.disabledAction : undefined}
            disabled={isApplying || isEnteringUsername}
            aria-disabled={sourceRetryIsPending || undefined}
            aria-busy={sourceRetryIsPending || undefined}
            onClick={() => {
              if (sourceRetryIsPending) return

              sourceRetryFocusImageRef.current = sourceFailureImage
              selectImage(sourceFailureImage)
            }}
          >
            {sourceRetryIsPending ? (
              <SimpleSpinner size="small" aria-hidden="true" />
            ) : null}
            Try Again
          </Button>
        ),
      }
    }

    if (activeImageHasBlockingInvalidTags) {
      return {
        variant: 'error',
        message: tagging
          ? 'This tag has an invalid position. Remove it or reset crop & transform to continue.'
          : INVALID_TAG_RESET_WARNING,
      }
    }

    if (inactiveBlockingImageIndexes.length > 0) {
      return {
        variant: 'error',
        message: getInactiveInvalidTagWarning(
          inactiveBlockingImageIndexes,
          tagging !== undefined,
        ),
        action: (
          <Button
            size="small"
            disabled={isApplying || isEnteringUsername}
            onClick={() => reviewImage(inactiveBlockingImageIndexes[0])}
          >
            Review Image {inactiveBlockingImageIndexes[0] + 1}
          </Button>
        ),
      }
    }

    return null
  })()
  const invalidTagImagePositionsKey =
    workspaceResult.state === 'blocked'
      ? workspaceResult.imageIndexes
          .map((imageIndex) => imageIndex + 1)
          .join(',')
      : ''
  const visibleErrors = React.useMemo<
    readonly ImageWorkspaceTrackingVisibleError[]
  >(() => {
    const errors: ImageWorkspaceTrackingVisibleError[] = []

    if (status.state === 'error') {
      errors.push({ errorType: status.errorType })
    } else if (editorIssue) {
      errors.push({
        errorType:
          editorDraftValidity?.errorType === 'imageLoadFailed'
            ? 'imageLoadFailed'
            : 'outputSizeInvalid',
        affectedImagePosition: session.activeIndex + 1,
      })
    }

    images.forEach((image, imageIndex) => {
      const affectedImagePosition = imageIndex + 1
      if (
        image.sourceStatus === 'error' &&
        !(
          sourceRetryIsPending &&
          affectedImagePosition === sourceFailureImageIndex + 1
        )
      ) {
        errors.push({
          errorType: 'imagePreparationFailed',
          affectedImagePosition,
        })
      }
    })
    if (
      sourceFailureImageIndex >= 0 &&
      !sourceRetryIsPending &&
      images[sourceFailureImageIndex]?.sourceStatus !== 'error'
    ) {
      errors.push({
        errorType: 'imagePreparationFailed',
        affectedImagePosition: sourceFailureImageIndex + 1,
      })
    }

    if (invalidTagImagePositionsKey) {
      invalidTagImagePositionsKey.split(',').forEach((imagePosition) => {
        errors.push({
          errorType: 'invalidTagPosition',
          affectedImagePosition: Number(imagePosition),
        })
      })
    }

    return errors
  }, [
    editorDraftValidity?.errorType,
    editorIssue,
    images,
    invalidTagImagePositionsKey,
    session.activeIndex,
    sourceFailureImageIndex,
    sourceRetryIsPending,
    status,
  ])

  React.useEffect(() => {
    trackingSession?.visibleErrorsChanged(visibleErrors)
  }, [trackingSession, visibleErrors])

  return (
    <>
      <Dialog.Header className={styles.header}>
        <Dialog.Title>Edit Media</Dialog.Title>
        <Dialog.CloseButton
          ref={readyCloseButtonRef}
          onClick={onCloseButtonClick}
        />
      </Dialog.Header>

      <Dialog.Body className={styles.body}>
        {workspaceNotice ? (
          <Notice variant={workspaceNotice.variant} className={styles.notice}>
            <Notice.Text>{workspaceNotice.message}</Notice.Text>
            {workspaceNotice.action ? (
              <Notice.Actions>{workspaceNotice.action}</Notice.Actions>
            ) : null}
          </Notice>
        ) : null}

        <div
          id={editorPanelId}
          className={styles.workspaceSurface}
          role="tabpanel"
          aria-labelledby={activeFilmstripTabId}
        >
          {editorSurface}
        </div>

        <VisuallyHidden as="output" aria-live="polite" aria-atomic="true">
          {pendingImageIndex >= 0
            ? `Preparing image ${pendingImageIndex + 1} of ${session.items.length}`
            : sourceFailureImageIndex >= 0
              ? `Image ${sourceFailureImageIndex + 1} could not be prepared. Select Try Again to retry.`
              : `${videoIsActive ? 'Video' : 'Image'} ${session.activeIndex + 1} of ${getImageWorkspaceItemCount(session)} selected`}
        </VisuallyHidden>

        {isApplying ? (
          <VisuallyHidden as="output" aria-live="polite" aria-atomic="true">
            Applying image and metadata changes.
          </VisuallyHidden>
        ) : null}
      </Dialog.Body>

      <Dialog.Footer showSeparator={false}>
        <Button
          variant="secondary"
          onClick={() => requestDismiss('cancelButton')}
        >
          Cancel
        </Button>
        <VisuallyHidden as="output" aria-live="polite" aria-atomic="true">
          {dirtyItemCount > 0
            ? `${dirtyItemCount} ${dirtyItemCount === 1 ? 'item' : 'items'} changed`
            : ''}
        </VisuallyHidden>
        {applyDisabledReason ? (
          <Tooltip content={applyDisabledReason}>{applyButton}</Tooltip>
        ) : (
          applyButton
        )}
        {applyDisabledReason ? (
          <VisuallyHidden id={applyDisabledReasonId}>
            {applyDisabledReason}
          </VisuallyHidden>
        ) : null}
      </Dialog.Footer>

      <ComposerMediaEditorDiscardDialog
        open={discardConfirmationOpen}
        changedItemCount={dirtyItemCount}
        getReturnFocusTarget={() => {
          const target = discardReturnFocusRef.current
          discardReturnFocusRef.current = null
          return target
        }}
        onOpenChange={(nextOpen) => {
          setDiscardConfirmationOpen(nextOpen)
          if (!nextOpen) pendingDismissMethodRef.current = null
        }}
        onDiscard={() => {
          const closeMethod = pendingDismissMethodRef.current ?? 'cancelButton'
          discardReturnFocusRef.current = null
          trackingSession?.close(
            {
              closeReason: 'discarded',
              closeMethod,
            },
            changeSummary,
          )
          onCancel()
        }}
      />
    </>
  )
})

type ComposerImageWorkspacePreparationContentProps = Extract<
  ComposerImageWorkspacePreparation,
  { status: 'error' }
> &
  Readonly<{
    onCancel: (method: ImageWorkspaceDismissMethod) => void
    onCloseButtonClick: () => void
  }>

const ComposerImageWorkspacePreparationContent = ({
  onRetry,
  onUsePreviousEditor,
  onCancel,
  onCloseButtonClick,
}: ComposerImageWorkspacePreparationContentProps): JSX.Element => (
  <>
    <Dialog.Header>
      <Dialog.Title>Edit Media</Dialog.Title>
      <Dialog.CloseButton onClick={onCloseButtonClick} />
    </Dialog.Header>
    <Dialog.Body className={styles.preparationErrorBody}>
      <output aria-live="polite">
        <Text color="critical">
          {onUsePreviousEditor
            ? 'The attached media could not be prepared for editing. Try again, or use the previous editor.'
            : 'The attached media could not be prepared for editing. Try again.'}
        </Text>
      </output>
    </Dialog.Body>
    <Dialog.Footer className={styles.preparationErrorFooter}>
      <Button variant="secondary" onClick={() => onCancel('cancelButton')}>
        Cancel
      </Button>
      <Button variant="secondary" onClick={onRetry}>
        Try Again
      </Button>
      {onUsePreviousEditor ? (
        <Button onClick={onUsePreviousEditor}>Use Previous Editor</Button>
      ) : null}
    </Dialog.Footer>
  </>
)

const ComposerImageWorkspaceLoadingContent = ({
  onCloseButtonClick,
}: Readonly<{ onCloseButtonClick: () => void }>): JSX.Element => (
  <>
    <Dialog.Header>
      <Dialog.Title>Edit Media</Dialog.Title>
      <Dialog.CloseButton onClick={onCloseButtonClick} />
    </Dialog.Header>
    <Dialog.Body className={styles.preparationLoadingBody}>
      <SimpleSpinner size="large" aria-label="Preparing media for editing" />
    </Dialog.Body>
  </>
)

export const ComposerImageWorkspace = ({
  open,
  initialTask,
  tagging,
  preparation,
  getReturnFocusTarget,
  trackingSession,
  onCancel,
  ...contentProps
}: ComposerImageWorkspaceProps): JSX.Element => {
  const taggingRef = React.useRef<ComposerImageUserTaggingHandle>(null)
  const contentRef = React.useRef<ComposerImageWorkspaceContentHandle>(null)
  const closeButtonClickedRef = React.useRef(false)
  const wasPreparingRef = React.useRef(preparation !== undefined)
  const resolvedInitialTask =
    initialTask ?? (tagging?.initialMode ? 'tagging' : 'edit')

  const requestDismiss = (method: ImageWorkspaceDismissMethod): void => {
    if (contentRef.current) {
      contentRef.current.requestDismiss(method)
      return
    }

    trackingSession?.close({
      closeReason: 'cancelled',
      closeMethod: method,
    })
    onCancel()
  }

  const handleEscapeKeyDown = (event: KeyboardEvent): void => {
    event.preventDefault()

    if (taggingRef.current?.cancelPlacement()) return

    requestDismiss('escapeKey')
  }

  const handleOpenAutoFocus = (event: Event): void => {
    if (!preparation && contentRef.current?.focusInitialTask()) {
      event.preventDefault()
    }
  }

  React.useLayoutEffect(() => {
    const wasPreparing = wasPreparingRef.current
    wasPreparingRef.current = preparation !== undefined
    if (!open || preparation || !wasPreparing) return

    contentRef.current?.focusInitialTask()
  }, [open, preparation])

  const restoreFocusOnClose = (event: Event): void => {
    if (open) {
      event.preventDefault()
      return
    }

    const target = getReturnFocusTarget?.()
    if (!target?.isConnected) return

    event.preventDefault()
    target.focus()
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          const closeMethod = closeButtonClickedRef.current
            ? 'closeButton'
            : 'outsideClick'
          closeButtonClickedRef.current = false
          requestDismiss(closeMethod)
        }
      }}
    >
      <Dialog.Content
        size="maximize"
        className={styles.dialogContent}
        aria-describedby={undefined}
        onEscapeKeyDown={handleEscapeKeyDown}
        onPointerDownOutside={(event) => {
          const { button, ctrlKey } = event.detail.originalEvent
          const isContextMenuClick =
            button === 2 || (button === 0 && ctrlKey === true)
          if (isContextMenuClick) return

          event.preventDefault()
          requestDismiss('outsideClick')
        }}
        onOpenAutoFocus={handleOpenAutoFocus}
        onCloseAutoFocus={restoreFocusOnClose}
      >
        {open ? (
          preparation?.status === 'error' ? (
            <ComposerImageWorkspacePreparationContent
              {...preparation}
              onCancel={requestDismiss}
              onCloseButtonClick={() => {
                closeButtonClickedRef.current = true
              }}
            />
          ) : preparation?.status === 'loading' ? (
            <ComposerImageWorkspaceLoadingContent
              onCloseButtonClick={() => {
                closeButtonClickedRef.current = true
              }}
            />
          ) : (
            <ComposerImageWorkspaceContent
              ref={contentRef}
              {...contentProps}
              initialTask={resolvedInitialTask}
              tagging={tagging}
              taggingRef={taggingRef}
              trackingSession={trackingSession}
              onCloseButtonClick={() => {
                closeButtonClickedRef.current = true
              }}
              onCancel={onCancel}
            />
          )
        ) : null}
      </Dialog.Content>
    </Dialog>
  )
}
